<script lang="ts">
  import type { FontWeightType } from "@autouncle/types"

  export let weight: FontWeightType = "regular"
  export let color: "default" | "body" | "positive" | "link" = "default"
  export let font:
    | "body-base"
    | "body-small"
    | "body-xsmall"
    | "heading-1"
    | "heading-2" = "body-base"
  export let align: "left" | "center" | "right" = "left"
  export let lineClamp: boolean = false
  export let lineClampLines: number | undefined = undefined
  export let ellipsis: boolean = false
  export let role: HTMLDivElement["role"] | undefined = undefined
  export let id: HTMLDivElement["id"] | undefined = undefined
  export let ariaLevel: 1 | 2 | 3 | 4 | 5 | 6 | undefined = undefined

  const textClasses = [
    "au-text",
    ellipsis && "au-ellipsis",
    lineClamp && "au-line-clamp",
  ]
    .filter(Boolean)
    .join(" ")
  const style = lineClamp
    ? lineClampLines
      ? `--au-text-line-clamp: ${lineClampLines};`
      : `--au-text-line-clamp: var(--au-line-clamp, 2);`
    : ""
</script>

<div
  {id}
  class={textClasses}
  data-weight={weight}
  data-align={align}
  data-color={color}
  data-font={font}
  {style}
  {role}
  aria-level={ariaLevel}
>
  <slot />
</div>

<style>
  .au-text[data-weight="regular"] {
    font-weight: var(--au-text-weight-regular);
  }
  .au-text[data-weight="medium"] {
    font-weight: var(--au-text-weight-medium);
  }
  .au-text[data-weight="bold"] {
    font-weight: var(--au-text-weight-bold);
  }
  .au-text[data-color="default"] {
    color: var(--au-text-color-default);
  }
  .au-text[data-color="body"] {
    color: var(--au-text-color-body);
  }
  .au-text[data-color="positive"] {
    color: var(--au-text-color-positive);
  }
  .au-text[data-color="link"] {
    color: var(--au-text-color-link);
  }
  .au-text[data-font="heading-1"] {
    font-size: var(--au-text-heading-1-font-size);
    line-height: var(--au-text-heading-1-line-height);
  }
  .au-text[data-font="heading-2"] {
    font-size: var(--au-text-heading-2-font-size);
    line-height: var(--au-text-heading-2-line-height);
  }
  .au-text[data-font="body-base"] {
    font-size: var(--au-text-body-base-font-size);
    line-height: var(--au-text-body-base-line-height);
  }
  .au-text[data-font="body-small"] {
    font-size: var(--au-text-body-small-font-size);
    line-height: var(--au-text-body-small-line-height);
  }
  .au-text[data-font="body-xsmall"] {
    font-size: var(--au-text-body-xsmall-font-size);
    line-height: var(--au-text-body-xsmall-line-height);
  }
  .au-text[data-align="left"] {
    text-align: left;
  }

  .au-text[data-align="center"] {
    text-align: center;
  }

  .au-text[data-align="right"] {
    text-align: right;
  }

  /**
  * @see https://css-tricks.com/line-clampin/
  * Rest of neccessary styles are applied via style attribute
  */
  .au-line-clamp {
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    line-clamp: var(--au-text-line-clamp);
    -webkit-line-clamp: var(--au-text-line-clamp);
  }

  .au-ellipsis {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
</style>
